<script setup lang="ts">
import { apiGetTheaterInfo } from '@/apis/theater'
import { showToast } from 'vant'
import { computed, ref, watch } from 'vue'
import type { IResTheaterInfo } from '@/types/apis/theater'

const show = defineModel<boolean>('show')
const props = defineProps<{
  scriptId: number
  activeEpisodeId?: number
}>()

const emit = defineEmits<{
  (e: 'update:show', value: boolean): void
  (e: 'select-episode', episodeId: number): void
}>()

const theaterInfo = ref<IResTheaterInfo>()
const loading = ref(false)

const popupShow = computed({
  get: () => show.value,
  set: (value: boolean) => (show.value = value),
})

const playEpisodeIndex = computed(() => {
  if (!theaterInfo.value?.Episodes?.length || !props.activeEpisodeId) return 0
  const targetIndex = theaterInfo.value.Episodes.findIndex(item => item.Id === props.activeEpisodeId)
  return targetIndex < 0 ? 0 : targetIndex
})

const fetchTheaterInfo = async () => {
  if (!props.scriptId || loading.value) return
  loading.value = true
  try {
    const response = await apiGetTheaterInfo({ scriptId: props.scriptId })
    theaterInfo.value = response.Body
  } catch {
    showToast('加载失败，请重试')
  } finally {
    loading.value = false
  }
}

watch(
  () => [show.value, props.scriptId] as const,
  ([visible, scriptId]) => {
    if (!visible || !scriptId) return
    fetchTheaterInfo()
  },
  { immediate: true },
)

const handleEpisodeSelect = (episodeId: number, hasVideo: boolean) => {
  if (!hasVideo) {
    showToast('该集暂无视频内容')
    return
  }
  emit('select-episode', episodeId)
  show.value = false
}
</script>

<template>
  <van-popup
    v-model:show="popupShow"
    position="bottom"
    closeable
    class="h-[80vh] rounded-t-[20px] bg-gray-900"
    @touchstart.stop
  >
    <div class="flex h-[80vh] flex-col space-y-8 bg-gray-900 p-4 text-white">
      <div class="flex flex-col gap-4">
        <div class="mb-2 flex items-center gap-2 break-all text-white uppercase">
          <span class="text-xl font-bold">{{ theaterInfo?.Name || '剧集' }}</span>
          <span class="text-xs text-gray-400">全{{ theaterInfo?.TotalEpisodes || 0 }}集</span>
        </div>
        <div class="mb-3 flex flex-col items-start justify-center gap-4">
          <div class="flex gap-2">
            <span
              v-for="tag in theaterInfo?.CategoryTags || []"
              :key="tag.CategoryId"
              class="rounded-md bg-gray-800 px-3 py-1 text-xs text-gray-300"
            >
              {{ tag.Name }}
            </span>
          </div>
        </div>
        <div class="text-justify text-sm opacity-80">
          {{ theaterInfo?.Description || '暂无简介' }}
        </div>
      </div>

      <h2 class="mb-4 text-base font-bold text-white">剧集列表</h2>
      <div class="flex-1 overflow-y-auto">
        <div class="grid grid-cols-7 gap-2">
          <button
            v-for="(episode, index) in theaterInfo?.Episodes || []"
            :key="episode.Id"
            class="relative flex-1 cursor-pointer rounded-lg border bg-gray-800 py-3 text-sm text-white transition-colors active:bg-gray-700"
            :class="[
              index === playEpisodeIndex ? 'bg-purple-60/20 text-purple-600 font-bold' : '',
              !episode.VideoUrl ? 'bg-yellow-400/5 border-yellow-400/10' : 'border-gray-800',
            ]"
            @click="handleEpisodeSelect(episode.Id, !!episode.VideoUrl)"
          >
            <div :class="{ 'opacity-40': !episode.VideoUrl }" class="flex flex-col items-center">
              <span>{{ episode.SequenceNumber }}</span>
            </div>
            <span v-if="!episode.IsFree" class="absolute top-0.5 right-1 text-[10px] font-bold text-yellow-400 italic"
              >VIP</span
            >
          </button>
        </div>
      </div>
    </div>
  </van-popup>
</template>
